import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import ArcadeMark from '../ArcadeMark';
import {
  LayoutDashboard,
  Gamepad2,
  Receipt,
  Users,
  Factory,
  List,
  Settings,
  LogOut,
  Moon,
  Sun,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Globe,
  Share2,
  Wallet,
  Cpu,
} from 'lucide-react';

const Sidebar = () => {
  const { user, logout, t, theme, toggleTheme, language, setLanguage, isAdmin, tenantName, appVersion, BACKEND_URL} = useApp();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/cabinets', icon: Gamepad2, label: t('nav.cabinets') },
    { path: '/operations', icon: Receipt, label: t('nav.operations') },
    { path: '/investors', icon: Wallet, label: t('nav.investors') },
    { path: '/spare-parts', icon: Cpu, label: t('nav.spareParts') },
    ...(isAdmin ? [{ path: '/users', icon: Users, label: t('nav.users') }] : []),
    { path: '/shares', icon: Share2, label: t('nav.shares') },
    { path: '/vendors', icon: Factory, label: t('nav.vendors') },
    { path: '/lists', icon: List, label: t('nav.lists') },
    { path: '/settings', icon: Settings, label: t('nav.settings') },
  ];

  const NavContent = () => (
    <>
      {/* Logo — marquee plate */}
      <div className="p-4 border-b border-border relative">
        <div className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-primary via-secondary to-transparent" />
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-background/60 border border-primary/40 flex items-center justify-center glow-amber animate-flicker">
            <ArcadeMark className="w-6 h-6 text-primary" />
          </div>
          {!collapsed && (
            <div className="flex flex-col leading-none">
              <span className="font-display font-bold text-lg tracking-[0.08em]">{(tenantName || 'ARCADE').toUpperCase()}</span>
              <span className="eyebrow text-[9px] text-secondary mt-1">Arcade Fleet</span>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `group relative flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200 ${
                isActive
                  ? 'bg-primary/12 text-primary glow-amber'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              } ${collapsed ? 'justify-center' : ''}`
            }
          >
            {({ isActive }) => (
              <>
                {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-0.5 rounded-full bg-primary shadow-[0_0_8px_hsl(var(--primary))]" />}
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span className="font-medium text-sm">{item.label}</span>}
              </>
            )}
          </NavLink>
        ))}

        {/* Version courante (sous Settings) */}
        {collapsed ? (
          <p className="mt-2 pt-2 border-t border-border/50 text-center eyebrow text-[9px] text-muted-foreground/60">
            {appVersion ? `v${appVersion}` : ''}
          </p>
        ) : (
          <p className="mt-2 pt-2 border-t border-border/50 px-3 text-[10px] font-mono text-muted-foreground/60 leading-tight">
            Arcade Fleet Manager<br />
            <span className="text-primary/70">{appVersion ? `v${appVersion}` : '—'}</span>
          </p>
        )}
      </nav>

      {/* Bottom section */}
      <div className="p-4 border-t border-border space-y-3">
        {/* Theme & Language toggles */}
        <div className={`flex ${collapsed ? 'flex-col' : ''} gap-2`}>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="w-full"
            data-testid="theme-toggle-btn"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          {!collapsed && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="w-full" data-testid="language-toggle-btn">
                  <Globe className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLanguage('en')} data-testid="lang-en">
                  {t('settings.english')} {language === 'en' && '✓'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('fr')} data-testid="lang-fr">
                  {t('settings.french')} {language === 'fr' && '✓'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className={`w-full ${collapsed ? 'justify-center p-2' : 'justify-start'} h-auto py-2`}
              data-testid="user-menu-btn"
            >
              <Avatar className="w-8 h-8">
                <AvatarImage src={user?.avatar ? `${user.avatar}` : undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {user?.nickname?.slice(0, 2).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              {!collapsed && (
                <div className="ml-3 text-left">
                  <p className="text-sm font-medium">{user?.nickname}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.profile_type}</p>
                </div>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
              <Settings className="w-4 h-4 mr-2" />
              {t('nav.settings')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-menu-item">
              <LogOut className="w-4 h-4 mr-2" />
              {t('nav.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Collapse button (desktop only) */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full border border-border bg-background hidden lg:flex"
        data-testid="collapse-sidebar-btn"
      >
        {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
      </Button>
    </>
  );

  return (
    <>
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setMobileOpen(true)}
        className="fixed top-4 left-4 z-50 lg:hidden"
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-6 h-6" />
      </Button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border flex flex-col transform transition-transform duration-300 lg:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileOpen(false)}
          className="absolute top-4 right-4"
          data-testid="close-mobile-menu-btn"
        >
          <X className="w-5 h-5" />
        </Button>
        <NavContent />
      </aside>

      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex fixed inset-y-0 left-0 z-30 bg-card border-r border-border flex-col transition-all duration-300 ${
          collapsed ? 'w-20' : 'w-64'
        }`}
      >
        <NavContent />
      </aside>

      {/* Spacer for content */}
      <div className={`hidden lg:block flex-shrink-0 transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'}`} />
    </>
  );
};

export default Sidebar;
